// Filename: gani_cfg.js
// Timestamp: 2018.05.05-00:25:14 (last modified)
// Author(s): bumblehead <chris@bumblehead.com>

import { Fragment } from 'inferno'
import { h } from 'inferno-hyperscript'
import {
  hypelns,
  hypelsvg
} from 'hypel'

import gnnodexdata from './gnnodexdata.js'
import gnnodexdatals from './gnnodexdatals.js'
import gnnodexui from './gnnodexui.js'
import gnnodex from './gnnodex.js'

import {
  gnenumKEY as KEY,
  gnenumNAME as NAME,
  gnenumXVNODE as XVNODE,
  gnenumCFGPROPTYPEPREFIXDIR as PREFIXDIR,
  gnenumNSSUBJCLASSNAMENODE as NSSUBJCLASSNAMENODE,
  gnenumLOGWARNINGS as LOGWARNINGS
} from './gnenum.js'

import {
  gnaccSubj as accSubj
} from './gnacc.js'

import {
  gnpathdef_findmatchingfullpathstr
} from './gnpathdef.js'

import {
  gnerr_cfgreqdfnnotfoundpagespecfromisopath,
  gnerr_cfgenvinvalidorigin,
  gnerr_cfgprefixdirinvalid,
  gnerrNodexInvalid as errNodexInvalid
} from './gnerr.js'

import {
  gnutilNonNullObjIs as utilNonNullObjIs
} from './gnutil.js'

const CLASSNAMEOVERRIDE = 'classNameOverride'

const isValidPrefixDir = dir => (
  typeof dir === 'string'
    && dir.endsWith('/')
    && dir.startsWith('/'))

const envLoggerCreate = (envlogger, levels) => {
  const loggerfnstub = (() => {})

  return Object.assign({
    levels: levels.reduce((m, l) => (m[l] = l,m), {})
  }, utilNonNullObjIs(envlogger) ? envlogger : {
    log: envlogger === true ? console.log : loggerfnstub,
    error: envlogger === true ? console.error : loggerfnstub,
    warn: envlogger === true ? console.warn : loggerfnstub
  })
}

export default (cfgglobal, cfgenv) => {
  const o = {}
  const boolOr = (arg, def) => (
    typeof arg === 'boolean' ? arg : def)
  const docHasRootId = doc =>
    doc && doc.getElementById('root')

  o.defautlLocale = 'eng-US'

  o.gaqlrun = cfgglobal.gaqlrun
  o.gaqlqueries = cfgglobal.gaqlqueries

  o.dom = cfgenv.dom || (typeof window === 'object' && window)

  // for expensive-to-generate messages, check for logging levels
  // ```
  // if (cfg.logger.levels[enumLOGREPORTS])
  //   cfg.logger.log(reportRender(rep))
  // ```
  o.logger = envLoggerCreate(cfgenv.logger,
    cfgenv.loglevels || [LOGWARNINGS])
  o.issinglepage = boolOr(cfgenv.issinglepage, false)
  o.jobqueue = []

  o.lastrenderid = 0

  // The app's root element is used with vdom diffing. One of two root
  // element types can be used,
  //  1) `<body />` (default: true)
  //  2) `<div id="root" class="root" />`
  //
  // server-environments rendering div-style root element must define
  // `isrootbody: false`
  o.isrootbody = typeof cfgenv.isrootbody === 'boolean'
    ? cfgenv.isrootbody
    : o.dom ? !docHasRootId(o.dom.window.document) : true

  o.version = cfgenv.version || '0.0.0'
  o.startts = cfgenv.startts || Date.now()
  o.cachemapisourlspecs = {}

  // origin, eg protocol + hostname "https://my.app.org"
  o.origin = cfgenv.origin
  if (!o.origin)
    throw gnerr_cfgenvinvalidorigin()

  o.cfgenv = cfgenv

  // NEEDS MORE INFO 'prev' data for all nodes
  o.prev = {}

  // frames per millisecond, used for animated nodes
  o.fpms = cfgenv.fpms
  o.specpath = cfgenv.specpath || './spec'
  o.speccache = {}
  o[PREFIXDIR] = cfgenv[PREFIXDIR] || cfgglobal[PREFIXDIR] || ''
  if (o[PREFIXDIR] && !isValidPrefixDir(o[PREFIXDIR]))
    throw gnerr_cfgprefixdirinvalid(o[PREFIXDIR])

  // holds basic rules for each supported path and used to detect
  // route permissions and configurations before requesting
  // or constructing page objects
  o.pathdef = cfgglobal.pathdef || null

  // creates a lookup map of page objects; throw error for invalid objects
  o.nodexmap = (cfgglobal.nodes || []).concat([
    gnnodexdata, // default nodes provided
    gnnodexdatals,
    gnnodexui
  ]).reduce((xmap, nodex) => {
    const isvalid = Boolean(nodex && nodex.type)
    if (!isvalid)
      throw errNodexInvalid(nodex)

    xmap[nodex.type] = nodex[XVNODE]
      ? Object.assign({}, gnnodexui, nodex)
      : Object.assign({}, gnnodex, nodex)

    return xmap
  }, {})

  // env-customizable initsess and initcfg,
  // called for every init sequence
  o.initsess = cfgenv.initsess || (ss => ss)
  o.initcfg = cfgenv.initcfg || (cfg => cfg)

  // probelocaleenv called before initlocale
  // and is called only when locale not found in url or session
  o.probelocaleenv = cfgenv.probelocaleenv || (
    (ss, cfg) => ss.locale || cfg.defautlLocale)

  // initlocale called after probelocaleenv
  // and is called for every init sequence
  o.initlocale = cfgenv.initlocale || (cfg => cfg)

  // after long undevelopment, added condition check for cfg.manifest.appname
  o.pagemetaget = cfgglobal.pagemetaget || ((ss, cfg, subj, pagedirarr) => ({
    description: subj.gnmetadescription
      || (cfg.manifest && cfg.manifest.appdescription),
    title: typeof subj.gnmetatitle === 'string' ? subj.gnmetatitle : [
      (pagedirarr || [])
        .map(pnode => pnode.pathvanilla.replace(/\//i, ''))
        .filter(e => e)[0],
      (cfg.manifest && cfg.manifest.appname) || 'appname'
    ].filter(e => e).join(' - ')
  }))

  // pattern files usually available from network or filesystem
  o.pagespecfromisopath = cfgenv.pagespecfromisopath || (
    (sess, cfg, isopath) => {
      throw gnerr_cfgreqdfnnotfoundpagespecfromisopath(cfg, isopath)
    }),

  // if requested path not permitted or found, return default path
  o.pagepathdefaultget = cfgglobal.pagepathdefaultget || (
    (sess, cfg) => gnpathdef_findmatchingfullpathstr(
      cfg, cfg.pathdef, `${cfg.pathdef.pagearr[0].pathvanilla}/`))

  o.pagepathdefispermitted = cfgglobal.pagepathdefispermitted || (
    (sess, cfg, pathdef) => 'permission' in pathdef === false)

  o.xvnode = (ss, cfg, gr, nd, ndx, subj) => (
    subj.className &&
      (nd = nd.set(CLASSNAMEOVERRIDE, subj.className)),
    ndx.xvnode(ss, cfg, gr, nd, ndx, subj))

  o.hfrag = children => h(Fragment, {}, children)

  // in case there is churn around hyperscript libraries
  o.h = (h => (nodex, nd, subj = accSubj.get(nd, {})) => h({
    key: nd.get(KEY),
    ui: (nd.get(CLASSNAMEOVERRIDE)
      || subj[NSSUBJCLASSNAMENODE]
      || nd.get(NAME).replace(/_\d[^.]*--/, '')
    ) + '.' + nodex.type
  }))(hypelns(h))

  o.hsvg = () => hypelsvg(h)

  return o
}
